import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatCard from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Printer, FileText, IndianRupee, Wallet, Files, Upload,
  CheckCircle2, Clock, XCircle, TrendingUp, Crown, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const STATUS_STYLES = {
  completed: "bg-brand-green/10 text-brand-green border-brand-green/20",
  printing: "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  queued: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  awaiting_retailer_confirm: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const STATUS_ICON = {
  completed: CheckCircle2, printing: Clock, pending: Clock, queued: Clock,
  awaiting_retailer_confirm: AlertTriangle, cancelled: XCircle,
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const promises = [
          api.get("/dashboard/stats"),
          api.get("/dashboard/chart"),
          api.get("/orders?limit=5"),
        ];
        if (user.role === "retailer" || user.role === "distributor") {
          promises.push(api.get("/subscriptions/me").catch(() => ({ data: null })));
        }
        const [s, c, o, sub] = await Promise.all(promises);
        setStats(s.data);
        setChart(c.data);
        setOrders(o.data);
        if (sub) setSubscription(sub.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user.role]);

  const canPrint = user.role === "customer" || user.role === "retailer";
  const isRetailer = user.role === "retailer";
  const money = (v) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

  const subActive = subscription?.active;
  const subExpiresSoon = subActive && (new Date(subActive.expires_at) - Date.now()) < 5 * 24 * 3600 * 1000;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Subscription banner (retailer only) */}
      {isRetailer && !subActive && (
        <Card className="p-5 border-amber-500/30 bg-amber-500/5 flex flex-col md:flex-row md:items-center gap-4" data-testid="sub-banner-inactive">
          <div className="flex items-center gap-3 flex-1">
            <div className="h-10 w-10 rounded-lg bg-amber-500/15 text-amber-600 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="font-display font-semibold">Your shop is offline for walk-in customers</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Activate a subscription to accept walk-in QR uploads and start earning.
              </p>
            </div>
          </div>
          <Button className="bg-brand-green hover:bg-brand-green-hover" onClick={() => nav("/subscription")} data-testid="sub-banner-cta">
            <Crown size={15} className="mr-2" /> Activate now
          </Button>
        </Card>
      )}
      {isRetailer && subExpiresSoon && (
        <Card className="p-4 border-amber-500/30 bg-amber-500/5 flex items-center gap-3" data-testid="sub-banner-expiring">
          <Crown size={16} className="text-amber-600" />
          <p className="text-sm flex-1">
            <b>{subActive.plan.toUpperCase()} plan</b> expires {new Date(subActive.expires_at).toLocaleDateString()}.
          </p>
          <Button size="sm" variant="outline" onClick={() => nav("/subscription")}>Renew</Button>
        </Card>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Overview</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/orders")} data-testid="dashboard-view-orders-btn">
            <Files size={16} className="mr-2" /> All orders
          </Button>
          {canPrint && (
            <Button
              className="bg-primary hover:bg-brand-blue-hover"
              onClick={() => nav("/print")}
              data-testid="dashboard-new-print-btn"
            >
              <Upload size={16} className="mr-2" /> New print
            </Button>
          )}
        </div>
      </div>

      {/* Stat cards (Bento) */}
      {loading || !stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard icon={FileText} label="Today's Prints" value={stats.today_prints} sub="last 24h" accent="primary" testId="stat-today-prints" />
          <StatCard icon={Files} label="Total Pages" value={stats.total_pages} sub="lifetime" accent="purple" testId="stat-total-pages" />
          <StatCard icon={IndianRupee} label={user.role === "super_admin" ? "Platform Revenue" : "Total Spend"} value={money(stats.revenue)} sub="incl. GST" accent="green" testId="stat-revenue" />
          <StatCard icon={Wallet} label="Wallet Balance" value={money(stats.wallet_balance)} sub={<button onClick={() => nav("/wallet")} className="text-primary hover:underline">Recharge →</button>} accent="amber" testId="stat-wallet" />
        </div>
      )}

      {/* Chart + Printer status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-border bg-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Last 7 days</p>
              <h3 className="font-display text-xl font-semibold mt-1">Print activity</h3>
            </div>
            <Badge className="bg-brand-green/10 text-brand-green border border-brand-green/20 hover:bg-brand-green/10">
              <TrendingUp size={12} className="mr-1" /> Trending
            </Badge>
          </div>
          <div className="h-64" data-testid="dashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <defs>
                  <linearGradient id="fillPrints" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="prints" stroke="hsl(var(--chart-1))" fillOpacity={1} fill="url(#fillPrints)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-border bg-card">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Printer status</p>
          <h3 className="font-display text-xl font-semibold mt-1">
            {stats?.printer_status?.online ?? 0} online
          </h3>
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-background">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center">
                  <Printer size={18} />
                </div>
                <div>
                  <p className="text-sm font-medium">Online printers</p>
                  <p className="text-xs text-muted-foreground">Ready to print</p>
                </div>
              </div>
              <span className="text-2xl font-display font-semibold tabular-nums text-brand-green">
                {stats?.printer_status?.online ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-background">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted text-muted-foreground flex items-center justify-center">
                  <Printer size={18} />
                </div>
                <div>
                  <p className="text-sm font-medium">Total printers</p>
                  <p className="text-xs text-muted-foreground">All configured</p>
                </div>
              </div>
              <span className="text-2xl font-display font-semibold tabular-nums">
                {stats?.printer_status?.total ?? 0}
              </span>
            </div>
            {(user.role === "super_admin" || user.role === "retailer") && (
              <Button variant="outline" className="w-full" onClick={() => nav("/printers")} data-testid="dashboard-manage-printers-btn">
                Manage printers
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Recent orders */}
      <Card className="p-6 border-border bg-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Recent activity</p>
            <h3 className="font-display text-xl font-semibold mt-1">Recent orders</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={() => nav("/orders")} data-testid="dashboard-see-all-orders-btn">
            See all →
          </Button>
        </div>
        {orders.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm" data-testid="dashboard-orders-empty">
            No prints yet. {canPrint && <button className="text-primary hover:underline" onClick={() => nav("/print")}>Start printing →</button>}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {orders.map((o) => {
              const Icon = STATUS_ICON[o.status] || Clock;
              return (
                <div key={o.id} className="py-3 flex items-center gap-4" data-testid={`recent-order-${o.id}`}>
                  <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{o.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.total_pages}p · {o.copies}x · {o.color ? "Color" : "B&W"} · {o.paper_size}
                    </p>
                  </div>
                  <span className="text-sm font-medium tabular-nums">{money(o.cost)}</span>
                  <Badge variant="outline" className={STATUS_STYLES[o.status]}>
                    <Icon size={12} className="mr-1" />{" "}
                    {o.status === "awaiting_retailer_confirm" ? "Awaiting paper" : o.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
